import { Grid } from "@mui/material";
import { useSegments } from "api/segments";
import { EmptyState } from "components/EmptyState/EmptyState";
import { ReactComponent as Rows01Icon } from "components/Icons/rows-01.svg";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useParams } from "react-router-dom";
import {
  SEGMENTS_ASSOCIATED_ACHIEVEMENTS_LIST_ID,
  SEGMENTS_ASSOCIATED_CAMPAIGNS_LIST_ID,
  SEGMENTS_ASSOCIATED_REWARDS_LIST_ID,
} from "state/tables/types";
import { useStyles } from "../SingleSegment.styles";
import { AssociatedItems } from "./AssociatedItems";

export const Relations = ({
  lastRecalculation,
  showLastRecalculation = true,
}: {
  lastRecalculation?: string;
  showLastRecalculation?: boolean;
}) => {
  const { message } = useMessage();
  const classes = useStyles();
  const { listing: campaignsListing } = useListing(
    SEGMENTS_ASSOCIATED_CAMPAIGNS_LIST_ID,
  );
  const { listing: rewardsListing } = useListing(
    SEGMENTS_ASSOCIATED_REWARDS_LIST_ID,
  );
  const { listing: achievementsListing } = useListing(
    SEGMENTS_ASSOCIATED_ACHIEVEMENTS_LIST_ID,
  );

  const { id: segmentId } = useParams<{ id: string }>();
  const {
    useSegmentAssociatedCampaigns,
    useSegmentAssociatedAchievements,
    useSegmentAssociatedRewards,
  } = useSegments();

  const { data: campaignsData, isLoading: isCampaignsDataLoading } =
    useSegmentAssociatedCampaigns(segmentId as string, {
      order: campaignsListing.order || "desc",
      page: campaignsListing.page,
      rows: campaignsListing.rowsPerPage,
      sortKey: campaignsListing.property || "createdAt",
    });

  const { data: rewardsData, isLoading: isRewardsDataLoading } =
    useSegmentAssociatedRewards(segmentId as string, {
      order: rewardsListing.order || "desc",
      page: rewardsListing.page,
      rows: rewardsListing.rowsPerPage,
      sortKey: rewardsListing.property || "createdAt",
    });

  const { data: achievementsData, isLoading: isAchievementsDataLoading } =
    useSegmentAssociatedAchievements(segmentId as string, {
      order: achievementsListing.order || "desc",
      page: achievementsListing.page,
      rows: achievementsListing.rowsPerPage,
      sortKey: achievementsListing.property || "createdAt",
    });

  const associatedItemsExist =
    (campaignsData && campaignsData.items.length > 0) ||
    (rewardsData && rewardsData.items.length > 0) ||
    (achievementsData && achievementsData.items.length > 0);

  const isDataLoading =
    isCampaignsDataLoading || isRewardsDataLoading || isAchievementsDataLoading;

  return (
    <>
      {isDataLoading || associatedItemsExist ? (
        <Grid container pb={12}>
          <AssociatedItems
            data={campaignsData}
            columnName="campaignName"
            menuLabel="campaignDetails"
            tableName="associatedCampaigns"
            showLastRecalculation={showLastRecalculation}
            lastRecalculation={lastRecalculation}
            listingId={SEGMENTS_ASSOCIATED_CAMPAIGNS_LIST_ID}
          />
          <AssociatedItems
            data={rewardsData}
            columnName="rewardName"
            menuLabel="rewardDetails"
            tableName="associatedRewards"
            listingId={SEGMENTS_ASSOCIATED_REWARDS_LIST_ID}
          />
          <AssociatedItems
            data={achievementsData}
            columnName="achievementName"
            menuLabel="achievementDetails"
            tableName="associatedAchievements"
            listingId={SEGMENTS_ASSOCIATED_ACHIEVEMENTS_LIST_ID}
          />
        </Grid>
      ) : (
        <EmptyState
          icon={<Rows01Icon />}
          title={message({ id: "segmentRelationsEmptyStateTitle" })}
          description={message({ id: "segmentRelationsEmptyStateDesc" })}
          className={classes.emptyState}
        />
      )}
    </>
  );
};
